import { AppId, FCX, usePlatformContext } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { IFoodProfileMenu } from "@models/FoodProfileMenu";
import { IFoodProfileSummary } from "@models/NewFoodProfileSummary";
import { PermissionLevelEnum } from "@models/PermissionSection";
import Widget from "@components/Widget/Widget";
import ExternalProductLink from "@pages/Food/components/ExternalProductLink/ExternalProductLink";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import LocalPopularity from "@pages/Food/components/SectionMenu/LocalPopularity";
import PairedFlavors from "@pages/Food/components/SectionMenu/PairedFlavors";
import Versatility from "@pages/Food/components/SectionMenu/Versatility";
import { buildMenuTrendsKeywordUrl } from "@pages/Food/Food.helpers";
import FoodSectionTitle from "@pages/Food/refresh/components/FoodSectionTitle/FoodSectionTitle";
import FoodSectionMoreLink from "@pages/Food/refresh/components/FoodSectionMoreLink/FoodSectionMoreLink";
import FoodMenuPenetration from "@pages/Food/refresh/components/FoodMenuPenetration/FoodMenuPenetration";
import "./FoodMenuSection.scss";

interface Props {
    summary: IFoodProfileSummary;
    menu: IFoodProfileMenu;
}

/**
 * Menu section of the refreshed Food Profile: penetration chart, versatility, paired keywords and local popularity.
 * The three cards are the shared Menu widgets; only their layout and spacing are restyled here.
 * As on the legacy page, the chart (summary permission) and the cards (menu permission) are locked separately.
 */
const FoodMenuSection: FCX<Props> = ({ summary, menu }) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const menuTrends = products[AppId.MenuTrends];

    const { slices, macStage, suggestedMacKeywords, categoryName } = summary.data.data;
    const { menuVersatility, foodVersatility, pairedFlavors, hasLocalPopularity } = menu.data;

    const hasChartAccess = summary.data.permissionLevel !== PermissionLevelEnum.None;
    const hasMenuAccess = menu.permissionLevel !== PermissionLevelEnum.None;
    const hasMenuTrendsLink = !menuTrends.isDisabled && !!menuTrends.url;

    const requestTrial = () => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('mtd'));
    };

    return (
        <div className="FoodMenuSection" data-testid="blockFoodMenu">
            <FoodSectionTitle
                className="FoodMenuSection__title"
                title={`Menu: ${summary.name}`}
            />
            <div className="FoodMenuSection__card">
                <div
                    className={cn("FoodMenuSection__zone", !hasChartAccess && "is-blocked")}
                    data-testid="blockFoodMenuChartZone"
                >
                    <FoodMenuPenetration
                        className="FoodMenuSection__blur"
                        slices={slices}
                        macStage={macStage}
                        suggestedMacKeywords={suggestedMacKeywords}
                        categoryName={categoryName}
                    />
                    {!hasChartAccess && (
                        <PermissionOverlay
                            onContactUsClick={requestTrial}
                            testId="blockFoodMenuChartPermissionOverlay"
                        >
                            <div>Upgrade your subscription to unlock</div>
                            <div>insights about <b>{summary.name}</b></div>
                        </PermissionOverlay>
                    )}
                </div>
                <div
                    className={cn("FoodMenuSection__zone", !hasMenuAccess && "is-blocked")}
                    data-testid="blockFoodMenuWidgetsZone"
                >
                    <div className="FoodMenuSection__widgets">
                        <Widget
                            testId="blockFoodMenuVersatility"
                            className="FoodMenuSection__versatility"
                            headerContent={<div>VERSATILITY</div>}
                        >
                            <Versatility foodValue={foodVersatility} menuValue={menuVersatility}/>
                        </Widget>
                        <Widget
                            testId="blockFoodMenuPairedWith"
                            className="FoodMenuSection__paired-with"
                            headerContent={(
                                <>
                                    <div>PAIRED WITH</div>
                                    <ExternalProductLink
                                        appId={AppId.MenuTrends}
                                        path={`${process.env.REACT_APP_MT_BYO_LINK}${encodeURIComponent(menu.name)}`}
                                        testId="buttonFoodMenuPairedWithExternalLink"
                                    />
                                </>
                            )}
                        >
                            <PairedFlavors data={pairedFlavors}/>
                        </Widget>
                        {hasLocalPopularity && (
                            <LocalPopularity
                                className="FoodMenuSection__local-popularity"
                                word={menu.name}
                                permissionLevel={menu.permissionLevel}
                            />
                        )}
                    </div>
                    {!hasMenuAccess && (
                        <PermissionOverlay
                            onContactUsClick={requestTrial}
                            testId="blockFoodMenuPermissionOverlay"
                        >
                            <div>Upgrade your subscription to learn</div>
                            <div>how menus feature <b>{summary.name}</b></div>
                        </PermissionOverlay>
                    )}
                </div>
            </div>
            {/* Same rule as the "go to" links: no link when the user doesn't have Menu Trends. */}
            {hasMenuTrendsLink && (
                <FoodSectionMoreLink
                    url={buildMenuTrendsKeywordUrl(menuTrends.url as string, summary.name)}
                    text="More Menu Trends"
                    testId="linkFoodMenuMoreMenuTrends"
                />
            )}
        </div>
    );
};

export default FoodMenuSection;
